import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  EDisplayRuleComparedAgainst,
  EFieldDisplayComparedValueCheckedStatus,
  IFormFieldsDisplayModel,
  IFormFieldsDisplayRule,
} from '../../../models/form-fields-formly-display.model';
import {
  EFormBuilderDisplayComparatorActions,
  EFormBuilderDisplayConditionComparator,
  EFormBuilderDisplayFieldComparator,
} from '../../../models/form-builder-display-comparator.enum';
import {
  EIremboFormlyFieldSubTypes,
  EIremboFormlyFieldTypes,
} from '@irembo-andela/irembogov3-common';

export const setDisplayHideExpressionFromModel = (
  model: IFormFieldsDisplayModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  if (!model?.displayRules || !model.action || model.displayRules.length < 1) {
    delete field?.expressions?.['hide'];
    delete field?.props?.['displayRuleModel'];
    field.hide = undefined;
    return field;
  }
  const displayRuleAction: EFormBuilderDisplayComparatorActions = model.action;

  const displayRuleTypes: Record<
    EFormBuilderDisplayConditionComparator,
    IFormFieldsDisplayRule[]
  > = {
    [EFormBuilderDisplayConditionComparator.AND]: [],
    [EFormBuilderDisplayConditionComparator.OR]: [],
  };

  model.displayRules.forEach((rule: IFormFieldsDisplayRule) => {
    if (!rule.comparator) {
      rule.comparator = EFormBuilderDisplayConditionComparator.OR;
    }
    if (rule.comparator === EFormBuilderDisplayConditionComparator.OR) {
      displayRuleTypes[EFormBuilderDisplayConditionComparator.OR].push(rule);
    }
    if (rule.comparator === EFormBuilderDisplayConditionComparator.AND) {
      displayRuleTypes[EFormBuilderDisplayConditionComparator.AND].push(rule);
    }
  });

  if (field?.props) {
    field.props['displayRuleModel'] = model;
  } else {
    field.props = {
      displayRuleActions: model,
    };
  }

  let propsHideFunction = generateAndAndOrQueries(
    displayRuleTypes,
    model?.configuredFieldRootModelPath
  );

  if (displayRuleAction === EFormBuilderDisplayComparatorActions.SHOW) {
    propsHideFunction = `!(${propsHideFunction})`;
  }

  if (field?.expressions) {
    field.expressions['hide'] = <string>propsHideFunction;
  } else {
    field.expressions = {
      hide: <string>propsHideFunction,
    };
  }
  return field;
};

const generateAndAndOrQueries = (
  actionDisplayGroup: Record<
    EFormBuilderDisplayConditionComparator,
    IFormFieldsDisplayRule[]
  >,
  configuredFieldRootModelPath: string | null | undefined = undefined
): string => {
  const andQueries: string[] = [];
  actionDisplayGroup[EFormBuilderDisplayConditionComparator.AND].forEach(
    actionDisplayRule => {
      return displayExpressionBuilder(
        actionDisplayRule,
        andQueries,
        configuredFieldRootModelPath
      );
    }
  );
  const orQueries: string[] = [];
  actionDisplayGroup[EFormBuilderDisplayConditionComparator.OR].forEach(
    actionDisplayRule => {
      return displayExpressionBuilder(
        actionDisplayRule,
        orQueries,
        configuredFieldRootModelPath
      );
    }
  );

  if (orQueries.length < 1) {
    return andQueries.join(' && ');
  }

  if (andQueries.length < 1) {
    return orQueries.join(' || ');
  }

  return `(${orQueries.join(' || ')}) && (${andQueries.join(' && ')})`;
};

const displayExpressionBuilder = (
  displayRule: IFormFieldsDisplayRule,
  collationArray: string[],
  configuredFieldRootModelPath: string | null | undefined = undefined
): string[] => {
  const expCompareFieldRefPath: string | null | undefined =
    displayRule.compareField?.fieldRefPath;
  let expCondition = '';
  let expComparedValue = '';
  switch (displayRule.condition) {
    case EFormBuilderDisplayFieldComparator.IS_BOOLEAN_TRUE: {
      expCondition = '===';
      if (displayRule.comparedValues.isChecked != null) {
        expComparedValue = displayRule.comparedValues.isChecked
          ? 'true'
          : 'false';
        break;
      }
      expComparedValue = displayRule.comparedValues.isValue;
      break;
    }
    case EFormBuilderDisplayFieldComparator.IS_ABOVE: {
      expCondition = '>';
      expComparedValue = displayRule.comparedValues.isValue;
      break;
    }
    case EFormBuilderDisplayFieldComparator.IS_BELOW: {
      expCondition = '<';
      expComparedValue = displayRule.comparedValues.isValue;
      break;
    }
    case EFormBuilderDisplayFieldComparator.IS_EQUAL_TO: {
      expCondition = '===';
      expComparedValue = displayRule.comparedValues.isValue;
      break;
    }
    case EFormBuilderDisplayFieldComparator.IS_NOT_EQUAL_TO: {
      expCondition = '!==';
      expComparedValue = displayRule.comparedValues.isValue;
      break;
    }
  }

  if (
    displayRule.comparedAgainst === EDisplayRuleComparedAgainst.formStateValue
  ) {
    if (
      displayRule.comparedFormStateValuePath &&
      expCondition &&
      expComparedValue
    ) {
      let pathList = `formState?.${displayRule.comparedFormStateValuePath?.formStateKey}`;
      if (displayRule.comparedFormStateValuePath?.fieldKey) {
        pathList = `${pathList}?.${displayRule.comparedFormStateValuePath?.fieldKey}`;
      }
      pathList = `${pathList}?.${displayRule.comparedFormStateValuePath?.valuePath}`;
      collationArray.push(
        `${pathList} ${expCondition} ${formatComparedValue(expComparedValue)}`
      );
    }
    return collationArray;
  }

  if (
    displayRule.comparedAgainst === EDisplayRuleComparedAgainst.fieldValidity
  ) {
    const fieldRefPath = displayRule.compareField?.fieldRefPath;
    const booleanValue = displayRule.comparedValues.validity;
    if (fieldRefPath) {
      const parts = fieldRefPath.split('?.');
      const chainedStr = parts.map((part: any) => `get('${part}')`).join('?.');
      const validity = booleanValue ? 'valid' : 'invalid';
      const pathList = `field?.parent?.parent?.form?.${chainedStr}.${validity}`;
      collationArray.push(pathList);
    }
    return collationArray;
  }

  if (
    !(
      configuredFieldRootModelPath &&
      expCompareFieldRefPath &&
      expCondition &&
      expComparedValue
    )
  ) {
    return collationArray;
  }

  if (displayRule.compareFieldType === EIremboFormlyFieldTypes.multicheckbox) {
    if (
      displayRule.condition ===
      EFormBuilderDisplayFieldComparator.IS_NOT_EQUAL_TO
    ) {
      collationArray.push(
        `!${configuredFieldRootModelPath}?.${expCompareFieldRefPath}?.includes(${formatComparedValue(
          expComparedValue
        )})`
      );
    } else {
      collationArray.push(
        `${configuredFieldRootModelPath}?.${expCompareFieldRefPath}?.includes(${formatComparedValue(
          expComparedValue
        )})`
      );
    }
  } else if (
    displayRule.compareField &&
    displayRule.compareFieldType &&
    [
      EIremboFormlyFieldTypes.customdropdown,
      EIremboFormlyFieldTypes.customdropdownpaginated,
    ].includes(displayRule.compareFieldType as EIremboFormlyFieldTypes)
  ) {
    if (
      [
        EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET,
        EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET_PAGINATED,
      ].includes(displayRule.compareField.fieldSubType)
    ) {
      collationArray.push(
        `${configuredFieldRootModelPath}?.${expCompareFieldRefPath}?.value ${expCondition} '${displayRule.comparedValues.isValue.value}'`
      );
    } else {
      collationArray.push(
        `${configuredFieldRootModelPath}?.${expCompareFieldRefPath} ${expCondition} ${formatComparedValue(
          expComparedValue
        )}`
      );
    }
  } else {
    collationArray.push(
      `${configuredFieldRootModelPath}?.${expCompareFieldRefPath} ${expCondition} ${formatComparedValue(
        expComparedValue
      )}`
    );
  }

  return collationArray;
};

const formatComparedValue = (value: any) => {
  if (
    typeof value === 'number' ||
    value === 'null' ||
    value === 'undefined' ||
    value === 'true' ||
    value === 'false'
  ) {
    return value;
  }
  return `'${value}'`;
};
